import { FC, useCallback, useContext, useRef } from "react";
import i18n from "@/i18n";
import {
  AppSpecifier,
  RefSchemaItem,
  RowWithEmbeddedMetadata,
} from "@data-root/types";
import {
  SchemaItemProps,
  useAppContext,
  useField,
  useTableHeaders,
} from "@data-root/react";
import { Button, Carousel, Skeleton, Space, Image, Typography } from "antd";
import { View } from "@data-root/react";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { RefInputContext } from ".";
import CarouselArrows from "../../CarouselArrows";
import { Remarks } from "../../remarks";

interface CarouselItemProps {
  appSpecifier: AppSpecifier;
  tableId: string;
  row: RowWithEmbeddedMetadata;

  onEdit: (row: RowWithEmbeddedMetadata) => void;
  onDelete: (row: RowWithEmbeddedMetadata) => void;
}

const { Title, Text } = Typography;

const CarouselItem: FC<CarouselItemProps> = ({
  appSpecifier,
  tableId,
  row,
  onEdit,
  onDelete,
}) => {
  const {
    primaryHeader,
    secondaryHeader,
    imageUri: image,
  } = useTableHeaders(appSpecifier, tableId, row);

  return (
    <div>
      <View>
        <Image
          style={{
            backgroundColor: "#dddddd",
            height: 200,
            width: "100%",
            maxHeight: 200,
            objectFit: "cover",
          }}
          src={image}
        />

        <Text>{primaryHeader}</Text>
        <Text>{secondaryHeader}</Text>
        <Space.Compact>
          <Button icon={<EditOutlined />} onClick={() => onEdit(row)} />
          <Button icon={<DeleteOutlined />} onClick={() => onDelete(row)} />
        </Space.Compact>
      </View>
    </div>
  );
};

const CarouselItemSkeleton: FC = () => {
  return (
    <View>
      <div
        style={{
          width: "100%",
          height: 200,
        }}
      >
        <Skeleton.Image active />
      </div>
      <Skeleton.Input block active />
      <Skeleton active />
      <Space.Compact>
        <Button loading icon={<EditOutlined />} />
        <Button loading icon={<DeleteOutlined />} />
      </Space.Compact>
    </View>
  );
};

export const RefInputCarouselPresentation: FC<
  SchemaItemProps<RefSchemaItem>
> = ({ item }) => {
  const { appSpecifier } = useAppContext();

  const { loaded, displayName, remarks } = useField(item);

  const { ready, onAdd, onEdit, onDelete, multiple, rows } =
    useContext(RefInputContext)!;

  const carouselRef = useRef();

  const handleOnAdd = useCallback(() => {
    onAdd();
  }, [onAdd]);

  const handleOnEdit = useCallback(
    (row: RowWithEmbeddedMetadata) => {
      onEdit(row);
    },
    [onEdit]
  );

  const handleOnDelete = useCallback(
    (row: RowWithEmbeddedMetadata) => {
      onDelete(row);
    },
    [onDelete]
  );

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      {!loaded || !ready ? (
        <CarouselItemSkeleton />
      ) : (
        <>
          <CarouselArrows carouselRef={carouselRef} />
          <Carousel ref={carouselRef as any}>
            {rows.map(
              (row, index) =>
                index % 2 === 0 && (
                  <div className="carousel-row" key={row.id}>
                    <CarouselItem
                      appSpecifier={appSpecifier}
                      tableId={item.ref}
                      row={row}
                      onEdit={handleOnEdit}
                      onDelete={handleOnDelete}
                    />
                    {rows[index + 1] && (
                      <CarouselItem
                        appSpecifier={appSpecifier}
                        tableId={item.ref}
                        row={rows[index + 1]}
                        onEdit={handleOnEdit}
                        onDelete={handleOnDelete}
                      />
                    )}
                  </div>
                )
            )}
          </Carousel>
        </>
      )}

      <Button
        loading={!loaded}
        onClick={() => {
          if (loaded) handleOnAdd();
        }}
        type="primary"
      >
        {multiple ? i18n.t("add") : i18n.t("edit")}
      </Button>

      <Remarks texts={remarks} />
    </View>
  );
};
